import { useState, useEffect, useCallback } from 'react';
import { apiClient, API_CONFIG, API_ENDPOINTS } from '../config/api';
import { uploadAttachments } from '../utils/attachmentUpload';
import type {
  AppealRequest,
  AppealResponse,
  AppealListItem,
} from '../types/report';

export function useSubmitAppeal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (request: AppealRequest): Promise<AppealResponse['result'] | null> => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');
      const res = await fetch(`${API_CONFIG.baseURL}${API_ENDPOINTS.APPEAL.SUBMIT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(userId && { KEY_USER_ID: userId }),
        },
        credentials: 'include',
        body: JSON.stringify({
          sanctionId: Number(request.sanctionId),
          sanctionType: request.sanctionType,
          reason: request.reason,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error?.message ?? '이의제기 접수에 실패했습니다.');
      }

      const data = await res.json();
      const result = data.result;
      const resultId: number | undefined = result?.appealId ?? result?.id;

      // 2단계: 첨부파일 업로드
      if (request.attachments && request.attachments.length > 0 && resultId) {
        try {
          await uploadAttachments('APPEAL', resultId, request.attachments);
        } catch (uploadErr) {
          console.warn('첨부파일 업로드 실패:', uploadErr);
          // 본문은 이미 제출되었으므로 계속 진행
        }
      }

      return result;
    } catch (err: any) {
      setError(err.message ?? '이의제기 접수에 실패했습니다.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

export function useAppealList() {
  const [appeals, setAppeals] = useState<AppealListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .get<{ result: { appeals: AppealListItem[] } }>(API_ENDPOINTS.APPEAL.LIST)
      .then((res) => {
        if (!cancelled) setAppeals(res.result.appeals ?? []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '이의제기 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { appeals, loading, error };
}

export function useAppealDetail(id: number | null) {
  const [appeal, setAppeal] = useState<AppealResponse['result'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .get<{ result: AppealResponse['result'] }>(API_ENDPOINTS.APPEAL.DETAIL(id))
      .then((res) => {
        if (!cancelled) setAppeal(res.result);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '이의제기 상세를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return { appeal, loading, error };
}
